import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { billingService } from '../services/billing.service'

export const billingQueryKeys = {
  overview: ['billing', 'overview'] as const,
  invoices: ['billing', 'invoices'] as const,
}

export function useBillingOverview() {
  return useQuery({
    queryKey: billingQueryKeys.overview,
    queryFn: billingService.getOverview,
  })
}

export function useUserInvoicesQuery() {
  return useQuery({
    queryKey: billingQueryKeys.invoices,
    queryFn: billingService.getUserInvoices,
  })
}

export function useInitiateCheckoutMutation() {
  return useMutation({
    mutationFn: ({ planId, couponCode }: { planId: number; couponCode?: string }) =>
      billingService.initiateCheckout(planId, couponCode),
    onError: (error: Error) => toast.error(error.message || 'Failed to initialize checkout.'),
  })
}

export function useVerifySignatureMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { paymentId: string; subscriptionId: string; signature: string }) =>
      billingService.verifySignature(payload),
    onSuccess: () => {
      toast.success('Subscription activated successfully!')
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.overview })
    },
    onError: (error: Error) => toast.error(error.message || 'Payment signature verification failed.'),
  })
}

export function useChangePlanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (planId: number) => billingService.changePlan(planId),
    onSuccess: () => {
      toast.success('Plan change processed successfully.')
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.overview })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to change plan.'),
  })
}

export function useToggleAutoRenewMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (autoRenew: boolean) => billingService.toggleAutoRenew(autoRenew),
    onSuccess: (_, autoRenew) => {
      toast.success(`Auto-renewal ${autoRenew ? 'enabled' : 'disabled'}.`)
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.overview })
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update auto-renewal.'),
  })
}
